import React, { useState } from 'react';
import type { CampaignAudience, CampaignLang, CampaignText, MarketingCampaign } from '../../../api/services/marketingService';
import type { Tone } from '../../../components/ui/dg';

/**
 * Pièces communes aux écrans de campagnes (liste et page façon merge
 * request GitLab) : libellés, tons, dates relatives, avatars, icônes d'état.
 */

export const LANGS: { code: CampaignLang; label: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'fr', label: 'Français', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'en', label: 'English', dir: 'ltr' }
];

export const LANG_NAME: Record<CampaignLang, string> = { fr: 'français', ar: 'arabe', en: 'anglais' };

export const KIND_LABEL: Record<string, string> = {
  place: 'Lieu',
  weekend: 'Week-end',
  holiday: 'Fête',
  event: 'Événement',
  sport: 'Sport',
  safety: 'Sécurité',
  incentive: 'Incitation',
  custom: 'Libre'
};

export const KIND_TONE: Record<string, string> = {
  place: 'bg-sky-50 text-sky-700',
  weekend: 'bg-violet-50 text-violet-700',
  holiday: 'bg-amber-50 text-amber-800',
  event: 'bg-fuchsia-50 text-fuchsia-700',
  sport: 'bg-teal-50 text-teal-700',
  safety: 'bg-red-50 text-red-700',
  incentive: 'bg-indigo-50 text-indigo-700',
  custom: 'bg-gray-100 text-gray-700'
};

/** États, vus comme ceux d'une merge request : ouverte, approuvée, envoyée (fusionnée), fermée. */
export const STATE: Record<string, { label: string; tone: string; icon: 'open' | 'approved' | 'sending' | 'sent' | 'closed' }> = {
  pending: { label: 'Ouverte', tone: 'bg-green-50 text-green-700', icon: 'open' },
  approved: { label: 'Approuvée', tone: 'bg-indigo-50 text-indigo-700', icon: 'approved' },
  sending: { label: 'Envoi en cours', tone: 'bg-sky-50 text-sky-700', icon: 'sending' },
  sent: { label: 'Envoyée', tone: 'bg-violet-50 text-violet-700', icon: 'sent' },
  rejected: { label: 'Fermée', tone: 'bg-red-50 text-red-700', icon: 'closed' }
};

export const AUDIENCE_LABEL: Record<CampaignAudience, string> = {
  all: 'Tous les comptes',
  renters: 'Comptes sans annonce',
  owners: 'Propriétaires (au moins une annonce)',
  user: 'Une personne'
};

export const SELECTABLE_AUDIENCES: CampaignAudience[] = ['all', 'renters', 'owners'];

export const CHANNEL_LABEL: Record<string, string> = { push: 'Push', email: 'E-mail' };

/** État affiché dans la liste et la relecture. */
export const campaignState = (status: string): { label: string; tone: Tone } => (
  status === 'pending' ? { label: 'À approuver', tone: 'amber' }
    : status === 'approved' ? { label: 'Approuvée', tone: 'indigo' }
    : status === 'sending' ? { label: 'Envoi en cours', tone: 'indigo' }
    : status === 'sent' ? { label: 'Envoyée', tone: 'green' }
    : { label: 'Refusée', tone: 'grey' }
);

export const audienceText = (c: MarketingCampaign) =>
  c.audience === 'user' && c.target_user_name ? c.target_user_name
    : c.audience === 'all' ? 'tous les comptes'
    : c.audience === 'owners' ? 'les propriétaires'
    : c.audience === 'renters' ? 'les comptes sans annonce'
    : AUDIENCE_LABEL[c.audience] || '';

export const channelsText = (c: MarketingCampaign) => {
  const list = (c.channels || []).map((ch) => CHANNEL_LABEL[ch] || ch);
  return list.length === 2 ? `${list[0]} et ${list[1].toLowerCase()}` : list[0] || 'Aucun canal';
};

export const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

export const emptyText = (): CampaignText => ({ title: '', body: '', subject: '', html: '' });
export const emptyContent = (): Record<CampaignLang, CampaignText> => ({ fr: emptyText(), ar: emptyText(), en: emptyText() });

export const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const ago = (value?: string | null) => {
  if (!value) return '';
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'hier';
  if (days < 30) return `il y a ${days} j`;
  return `le ${new Date(value).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

export const ref = (c: MarketingCampaign) => (c.number ? `!${c.number}` : `#${c.id_campaign.slice(0, 6)}`);

const photoUrl = (url?: string | null) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return `${process.env.REACT_APP_API_URL || ''}${url.replace(/^\//, '')}`;
};

const TONES = ['bg-indigo-100 text-indigo-700', 'bg-amber-100 text-amber-800', 'bg-teal-100 text-teal-800', 'bg-rose-100 text-rose-700', 'bg-sky-100 text-sky-800', 'bg-violet-100 text-violet-700'];
const toneOf = (key: string) => TONES[key.split('').reduce((sum, c) => sum + c.charCodeAt(0), 0) % TONES.length];

/** Avatar : photo, sinon initiales ; `ai` dessine le relecteur IA ; `api` les propositions automatiques. */
export const Avatar = ({ name, photo, size = 24, ai = false, api = false, title }: { name?: string | null; photo?: string | null; size?: number; ai?: boolean; api?: boolean; title?: string }) => {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.4)) };
  const label = title || name || (ai ? 'IA Dingo' : api ? 'API Dingo' : 'Compte');
  if (ai) {
    return (
      <span title={label} style={style} className="inline-flex shrink-0 items-center justify-center rounded-full bg-indigo-600 font-bold text-white ring-2 ring-white">
        IA
      </span>
    );
  }
  if (api) {
    return (
      <span title={label} style={style} className="inline-flex shrink-0 items-center justify-center rounded-full bg-gray-900 font-bold text-white ring-2 ring-white">
        D
      </span>
    );
  }
  const src = photoUrl(photo);
  if (src && !broken) {
    return <img src={src} alt="" title={label} onError={() => setBroken(true)} style={style} className="shrink-0 rounded-full object-cover ring-2 ring-white" />;
  }
  const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';
  return (
    <span title={label} style={style} className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold ring-2 ring-white ${toneOf(name || '?')}`}>
      {initials}
    </span>
  );
};

/** Icône d'état, au trait — comme celles des merge requests. */
export const StateIcon = ({ status, className = 'h-4 w-4' }: { status: string; className?: string }) => {
  const kind = STATE[status]?.icon || 'open';
  const color = { open: 'text-green-600', approved: 'text-indigo-600', sending: 'text-sky-600', sent: 'text-violet-600', closed: 'text-red-600' }[kind];
  return (
    <svg className={`${className} ${color}`} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      {kind === 'open' && <><circle cx="6" cy="6" r="2.2" /><circle cx="6" cy="18" r="2.2" /><circle cx="18" cy="18" r="2.2" /><path d="M6 8.2v7.6M18 15.8V9a3 3 0 0 0-3-3h-4M13 4l-2 2 2 2" /></>}
      {kind === 'approved' && <><circle cx="12" cy="12" r="8.5" /><path d="M8.5 12.2l2.3 2.3 4.7-4.8" /></>}
      {kind === 'sending' && <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>}
      {kind === 'sent' && <><circle cx="6" cy="6" r="2.2" /><circle cx="6" cy="18" r="2.2" /><circle cx="18" cy="12" r="2.2" /><path d="M6 8.2v7.6M7.6 7.6c1.8 2.6 4.3 4.1 8.2 4.4" /></>}
      {kind === 'closed' && <><circle cx="12" cy="12" r="8.5" /><path d="M9 9l6 6M15 9l-6 6" /></>}
    </svg>
  );
};

export const StateBadge = ({ status }: { status: string }) => {
  const state = STATE[status] || STATE.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ${state.tone}`}>
      <StateIcon status={status} className="h-3.5 w-3.5" />
      {state.label}
    </span>
  );
};

export const Label = ({ children, tone = 'bg-gray-100 text-gray-700' }: { children: React.ReactNode; tone?: string }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{children}</span>
);
